// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Runtime Cognito configuration for the embedded, in-page auth flow
 * (ADR-014). Unlike the FAST baseline this replaces, there is no OAuth
 * `authority`/`redirect_uri` dance — the app talks to the Cognito
 * Identity Provider API directly (see lib/cognito/client.ts), so all it
 * needs is { region, userPoolId, clientId }.
 *
 * Configuration priority (highest to lowest), matching the FAST baseline's
 * established pattern:
 * 1. Environment variables (VITE_COGNITO_*)
 * 2. /aws-exports.json, fetched at runtime (deploy-time config, no rebuild)
 * 3. Hard failure — auth cannot work without a user pool + client id.
 */

import { parseSocialProviders, type SocialProvider } from "@bitebalance/auth-core"

export interface CognitoRuntimeConfig {
  region: string
  userPoolId: string
  clientId: string
  /**
   * Cognito managed-login domain (no scheme), e.g.
   * "bitebalance-…-eu-central-1.auth.eu-central-1.amazoncognito.com".
   * Only needed for the social sign-in OAuth flow; absent means the email/
   * password embedded flow only.
   */
  cognitoDomain?: string
  /**
   * Social providers the deployment has enabled (T18 follow-up). Empty by
   * default — the UI renders no social buttons, which is exactly today's
   * email/password-only experience.
   */
  socialProviders: SocialProvider[]
}

interface AwsExportsFile {
  /** e.g. "https://cognito-idp.eu-central-1.amazonaws.com/eu-central-1_xxxx" */
  authority?: string
  client_id?: string
  /** Cognito managed-login domain (host only). */
  cognito_domain?: string
  /** e.g. ["google", "apple"] — generated from config.yaml social flags. */
  social_providers?: string[]
}

let configPromise: Promise<AwsExportsFile | null> | null = null

async function loadAwsExports(): Promise<AwsExportsFile | null> {
  if (configPromise) return configPromise

  configPromise = (async () => {
    try {
      const response = await fetch("/aws-exports.json")
      if (!response.ok) {
        throw new Error(`Failed to load aws-exports.json: ${response.status}`)
      }
      return (await response.json()) as AwsExportsFile
    } catch (error) {
      console.error("Failed to load aws-exports.json:", error)
      return null
    }
  })()

  return configPromise
}

/** Parses `{region}` and `{userPoolId}` out of a Cognito issuer/authority URL. */
function parseAuthority(authority: string | undefined): { region?: string; userPoolId?: string } {
  if (!authority) return {}
  const match = authority.match(/cognito-idp\.([a-z0-9-]+)\.amazonaws\.com\/([\w-]+)/)
  if (!match) return {}
  return { region: match[1], userPoolId: match[2] }
}

export async function loadCognitoConfig(): Promise<CognitoRuntimeConfig> {
  const awsExports = await loadAwsExports()
  const fromAuthority = parseAuthority(awsExports?.authority)

  const region = import.meta.env.VITE_COGNITO_REGION || fromAuthority.region
  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID || fromAuthority.userPoolId
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || awsExports?.client_id

  if (!region || !userPoolId || !clientId) {
    throw new Error(
      "Missing Cognito configuration: expected VITE_COGNITO_REGION/VITE_COGNITO_USER_POOL_ID/" +
        "VITE_COGNITO_CLIENT_ID or a valid /aws-exports.json with `authority` + `client_id`."
    )
  }

  // Social sign-in is strictly additive config: a deployment without these
  // keys behaves exactly as before (no buttons, embedded auth only).
  const cognitoDomain: string | undefined =
    import.meta.env.VITE_COGNITO_DOMAIN || awsExports?.cognito_domain || undefined
  const socialProviders = parseSocialProviders(
    import.meta.env.VITE_SOCIAL_PROVIDERS
      ? String(import.meta.env.VITE_SOCIAL_PROVIDERS).split(",")
      : awsExports?.social_providers
  )

  return {
    region,
    userPoolId,
    clientId,
    cognitoDomain,
    // No OAuth endpoint ⇒ no social flow, regardless of the provider list.
    socialProviders: cognitoDomain ? socialProviders : [],
  }
}
